/**
 * Validação de identificadores brasileiros.
 *
 * Mora no domínio porque a web precisa validar enquanto o usuário digita, o
 * app de pátio precisa validar offline, e o servidor precisa validar de novo
 * antes de gravar. Uma implementação, um resultado.
 */
import { ErroDominio } from "./numero.js";
/** Tira pontuação e espaço, e sobe para maiúscula (CNPJ alfanumérico usa A–Z). */
export function limparDocumento(bruto) {
    return String(bruto ?? "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
}
/**
 * Valor do caractere no módulo 11: código ASCII menos 48.
 *
 * Herança do CNPJ alfanumérico (IN RFB 2.229/2024): dígitos mantêm o valor
 * ('0'→0, '9'→9) e letras continuam a contagem ('A'→17 … 'Z'→42). O mesmo
 * cálculo serve para CNPJ numérico, que é o caso particular sem letras.
 */
function valorDoCaractere(c) {
    return c.charCodeAt(0) - 48;
}
function fecharModulo11(soma) {
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
}
/**
 * Dígito do CNPJ: pesos de 2 a 9 CICLANDO da direita para a esquerda.
 *
 * Sobre 12 posições isso dá, da esquerda para a direita, 5,4,3,2,9,8,7,6,5,4,3,2
 * — o ciclo existe justamente porque o CNPJ é mais longo que a faixa de pesos.
 */
function digitoCnpj(valores) {
    let soma = 0;
    let peso = 2;
    for (let i = valores.length - 1; i >= 0; i--) {
        soma += valores[i] * peso;
        peso = peso === 9 ? 2 : peso + 1;
    }
    return fecharModulo11(soma);
}
/**
 * Dígito do CPF: pesos DECRESCENTES, sem ciclo.
 *
 * 10..2 no primeiro dígito e 11..2 no segundo. Reaproveitar o cálculo cíclico
 * do CNPJ aqui dá errado — cabe em 9 e 10 posições sem precisar reciclar, e o
 * peso da ponta esquerda seria 2 em vez de 10.
 */
function digitoCpf(valores) {
    let soma = 0;
    let peso = valores.length + 1;
    for (const v of valores) {
        soma += v * peso;
        peso--;
    }
    return fecharModulo11(soma);
}
/**
 * CNPJ — numérico ou alfanumérico.
 *
 * Desde julho de 2026 inscrições novas podem trazer letras nas 12 primeiras
 * posições; os 2 dígitos verificadores continuam numéricos. CNPJ antigo não
 * muda e continua válido — por isso a mesma função aceita os dois.
 */
export function ehCnpjValido(bruto) {
    const cnpj = limparDocumento(bruto);
    if (cnpj.length !== 14)
        return false;
    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj))
        return false;
    // Repetição total (00000000000000) passa no módulo 11 e não existe.
    if (/^(.)\1{13}$/.test(cnpj))
        return false;
    const base = [...cnpj.slice(0, 12)].map(valorDoCaractere);
    const dv1 = digitoCnpj(base);
    if (dv1 !== Number(cnpj[12]))
        return false;
    const dv2 = digitoCnpj([...base, dv1]);
    return dv2 === Number(cnpj[13]);
}
export function ehCpfValido(bruto) {
    const cpf = limparDocumento(bruto);
    if (!/^[0-9]{11}$/.test(cpf))
        return false;
    if (/^(\d)\1{10}$/.test(cpf))
        return false;
    const base = [...cpf.slice(0, 9)].map(Number);
    const dv1 = digitoCpf(base);
    if (dv1 !== Number(cpf[9]))
        return false;
    const dv2 = digitoCpf([...base, dv1]);
    return dv2 === Number(cpf[10]);
}
/** Aceita qualquer um dos dois, pelo comprimento. */
export function ehDocumentoValido(bruto) {
    const d = limparDocumento(bruto);
    if (d.length === 11)
        return ehCpfValido(d);
    if (d.length === 14)
        return ehCnpjValido(d);
    return false;
}
export function exigirDocumento(bruto, campo = "documento") {
    const limpo = limparDocumento(bruto);
    if (!ehDocumentoValido(limpo)) {
        throw new ErroDominio(`${campo}: "${bruto}" não é um CPF nem um CNPJ válido`);
    }
    return limpo;
}
export function formatarCnpj(bruto) {
    const c = limparDocumento(bruto);
    if (c.length !== 14)
        return bruto;
    return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}
export function formatarCpf(bruto) {
    const c = limparDocumento(bruto);
    if (c.length !== 11)
        return bruto;
    return `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}`;
}
export function formatarDocumento(bruto) {
    const d = limparDocumento(bruto);
    if (d.length === 11)
        return formatarCpf(d);
    if (d.length === 14)
        return formatarCnpj(d);
    return bruto;
}
/**
 * NCM — 8 dígitos.
 *
 * Não valida contra a tabela oficial de propósito: ela muda por ato normativo
 * várias vezes ao ano, e embutir uma cópia aqui garante ficar desatualizado.
 * O que se valida é a forma; a existência é problema do gateway fiscal, que
 * rejeita na emissão com mensagem própria.
 */
export function ehNcmValido(bruto) {
    return /^[0-9]{8}$/.test(String(bruto ?? "").replace(/\D/g, ""));
}
export function limparNcm(bruto) {
    return String(bruto ?? "").replace(/\D/g, "");
}
export function formatarNcm(bruto) {
    const n = limparNcm(bruto);
    if (n.length !== 8)
        return bruto;
    return `${n.slice(0, 4)}.${n.slice(4, 6)}.${n.slice(6)}`;
}
/** UFs brasileiras. */
export const UFS = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
    "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
    "SP", "SE", "TO",
];
export function ehUfValida(bruto) {
    return UFS.includes(String(bruto ?? "").toUpperCase());
}
//# sourceMappingURL=validacao.js.map